import { Component, inject, Input, SimpleChanges } from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { Subject, takeUntil } from 'rxjs';
import { SbiService } from '../../../../../../_services/sbi.service';
import { Sbi } from '../../../../../../models/sbi';
import { BaseComponent } from '../../../base/base.component';

type SoftSkill = {
  id: string;
  alias: string;
  filestorage: { path: string };
};

type TrendType = 'migliorative' | 'positive';

interface SoftSkillPayload {
  argument_id: number | null;
  name: string;
  percentage: number | null;
  type: TrendType;
}

@Component({
  selector: 'app-sbi-widget',
  templateUrl: './sbi-widget.component.html',
  styleUrls: ['./sbi-widget.component.css']
})
export class SbiWidgetComponent extends BaseComponent {
  @Input() chosenUser?: string;
  @Input() isVisible: boolean;
  @Input() isEdit: boolean = false;
  @Input() selectedReviewId: number;
  isLoading: boolean = true;
  public override router = inject(Router);

  sbis: Sbi[];
  migliorativeSbis: Sbi[];
  positiveSbis: Sbi[];
  lastReviewEndDate: string | null = null;
  lastReviewStartDate: string | null = null;
  filteredCardToEdited: number | null = null; // prenderà l'id del SBI da modificare
  filteredSbiEditedAlias: string = '';
  trendIsEdited: string | null = null;
  softSkills: SoftSkill[] = []; // elenco delle soft skills disponibili per la select
  private readonly destroy$: Subject<void> = new Subject<void>();
  lastReview: any = null;
  firstReviewSoftSkills: any = [];
  secondReviewSoftSkills: any = [];
  thirdReviewSoftSkills: any = [];

  migliorativeSoftSkills: any = [];
  positiveSoftSkills: any = [];

  trends: any = [
    { label: 'Capacità di negoziazione e mediazione', percentage: 50 },
    { label: 'Capacità di negoziazione e mediazione', percentage: 30 },
    { label: 'Capacità di negoziazione e mediazione', percentage: 20 }
  ];

  editorOpening: boolean = false;
  selectedTrend: TrendType = 'migliorative';

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '210px',
    minHeight: '0',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    defaultParagraphSeparator: '',
    sanitize: true,
    toolbarHiddenButtons: [
      [
        'insertImage',
        'insertVideo',
        'fontSize',
        'fontName',
        'subscript',
        'superscript',
        'htmlCode',
        'insertHorizontalRule',
        'clearFormatting'
      ]
    ]
  };

  constructor(private sbiService: SbiService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.isVisible &&
      (changes['chosenUser']?.currentValue || this.chosenUser) &&
      changes['selectedReviewId']?.currentValue
    ) {
      this.getSbiForReviewWidget();

      this.sbiService
        .getSoftSkills()
        .pipe(takeUntil(this.destroy$))
        .subscribe((data): void => {
          this.softSkills = data;
        });
    }
  }

  getSbiForReviewWidget(): void {
    const data = {
      chosen_user: this.chosenUser,
      selectedReviewId: this.selectedReviewId
    };
    this.sbiService
      .getSbiForReviewWidget(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any): void => {
        this.sbis = data.sbis;
        this.migliorativeSbis = data.migliorativeSbis;
        this.positiveSbis = data.positiveSbis;
        this.lastReviewStartDate = data.filteredDate?.start_date || null;
        this.lastReviewEndDate = data.filteredDate?.end_date || null;
        this.lastReview = data.lastReview || null;
        this.migliorativeSoftSkills = data.groupedSoftSkills.migliorative || [];
        this.positiveSoftSkills = data.groupedSoftSkills.positive || [];
        this.isLoading = false;
      });
  }

  allowEditFilteredCard(sbi: Sbi): void {
    this.filteredCardToEdited = sbi.id;
    this.filteredSbiEditedAlias = sbi.alias || '';
  }

  /**
   * Invia l'alias modificato per un widget (SBI) filtrato.
   *
   * Questa funzione verifica se i dati da modificare sono validi,
   * e in caso positivo chiama il servizio `updateAlias` per aggiornare
   * l'alias del widget corrispondente.
   * Al termine della chiamata:
   * - aggiorna l'oggetto `sbi` con il nuovo alias
   * - resetta i campi `filteredCardToEdited` e `filteredSbiEditedAlias`
   *
   * @param {Sbi} sbi - L'oggetto SBI da aggiornare localmente dopo la modifica dell'alias.
   * @returns {void}
   */
  sendEditedFilteredWidget(sbi: Sbi): void {
    if (!this.filteredCardToEdited || this.filteredSbiEditedAlias === '') {
      return;
    }

    const data = {
      alias: this.filteredSbiEditedAlias
    };

    this.sbiService
      .updateAlias(data, this.filteredCardToEdited)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any): void => {
        sbi.alias = this.filteredSbiEditedAlias;
        this.filteredCardToEdited = null;
        this.filteredSbiEditedAlias = '';
      });
  }

  editTrend(value: TrendType): void {
    this.trendIsEdited = value;

    const source =
      value === 'migliorative'
        ? this.migliorativeSoftSkills
        : this.positiveSoftSkills;

    const fillSkill = (target: any, sourceSkill: any) => {
      if (target?.patchValue) {
        // Se è un FormGroup
        target.patchValue({
          argument_id: sourceSkill?.argument_id ?? null,
          name: sourceSkill?.name ?? '',
          percentage: sourceSkill?.percentage ?? null,
          type: value
        });
      } else {
        // Se è un plain object
        Object.assign(target, {
          argument_id: sourceSkill?.argument_id ?? null,
          name: sourceSkill?.name ?? '',
          percentage: sourceSkill?.percentage ?? null,
          type: value
        });
      }
    };

    fillSkill(this.firstReviewSoftSkills, source[0] || {});
    fillSkill(this.secondReviewSoftSkills, source[1] || {});
    fillSkill(this.thirdReviewSoftSkills, source[2] || {});
  }

  saveTrend(value: TrendType): void {
    const skills: SoftSkillPayload[] = this.buildSkillsPayload(value);

    const data = { skills, type: value };

    this.sbiService
      .updateReviewSoftSkills(data, this.lastReview.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any): void => {
        this.lastReview = data.review;
        this.positiveSoftSkills = data.groupedSoftSkills.positive || [];
        this.migliorativeSoftSkills = data.groupedSoftSkills.migliorative || [];
        this.trendIsEdited = null;

        // Reset dei valori
        this.firstReviewSoftSkills = {
          argument_id: null,
          name: '',
          percentage: null,
          type: value
        };
        this.secondReviewSoftSkills = {
          argument_id: null,
          name: '',
          percentage: null,
          type: value
        };
        this.thirdReviewSoftSkills = {
          argument_id: null,
          name: '',
          percentage: null,
          type: value
        };
      });
  }

  private buildSkillsPayload(type: TrendType): SoftSkillPayload[] {
    const normalize = (s: any): SoftSkillPayload => {
      // se s è un FormGroup, usa s.getRawValue()
      const v = s?.getRawValue ? s.getRawValue() : s;

      const alias: string =
        this.softSkills.find((x: any): boolean => x.id === v?.argument_id)
          ?.alias ?? '';

      return {
        argument_id: v?.argument_id ?? null,
        name: alias,
        percentage: v?.percentage ?? null,
        type
      };
    };

    return [
      normalize(this.firstReviewSoftSkills),
      normalize(this.secondReviewSoftSkills),
      normalize(this.thirdReviewSoftSkills)
    ];
  }

  onPercentageChange(value: number, skillItem: any): void {
    if (value > 100) {
      value = 100;
    } else if (value < 0) {
      value = 0;
    }

    skillItem.percentage = value;
  }

  /**
   * Verifica se ci sono SBI da mostrare in base alla tipologia selezionata.
   * Se la tipologia selezionata è 'migliorative', controlla se
   * ci sono SBI migliorative disponibili, altrimenti controlla per SBI positive.
   */
  sbisIsNotEmpty(): boolean {
    if (this.selectedTrend === 'migliorative') {
      return this.migliorativeSbis?.length > 0;
    }
    return this.positiveSbis?.length > 0;
  }

  /**
   * Gestisce il click su una tendenza specifico ('migliorative' o 'positive').
   * Imposta la tendenza selezionato in modo da poter visualizzare le SBI corris
   */
  onTrendClick(trend: 'migliorative' | 'positive'): void {
    this.selectedTrend = trend;
  }

  /**
   * Apre l'editor per modificare la nota dell'ultima review.
   */
  onNoteEditClicked(): void {
    this.editorOpening = true;
  }

  /**
   * Gestisce il salvataggio della nota dell'ultima review.
   *
   * - Se `lastReview.note` è vuota o non definita, la funzione termina senza effettuare alcuna chiamata.
   * - In caso contrario, prepara un payload contenente la nota e invia una richiesta al servizio `sbiService`
   *   per aggiornare la review corrispondente a `lastReview.id`.
   * - La sottoscrizione alla chiamata HTTP viene automaticamente annullata quando `destroy$` emette,
   *   evitando memory leak.
   * - Alla fine del processo, chiude l'editor impostando `editorOpening` a `false`.
   *
   * @returns {void} Non restituisce alcun valore.
   */
  onNoteSaveClicked(): void {
    if (!this.lastReview.note) return;

    const data = {
      note: this.lastReview.note
    };

    this.sbiService
      .updateReviewNote(data, this.lastReview.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((): void => {});

    this.editorOpening = false;
  }

  sanitizeImg(path: string | null): SafeUrl {
    if (path === null || path === '') {
      return 'assets/images/default_images/default-project.png';
    }
    return this.utilityService.getSanitizeUrl(path);
  }

  async redirectTo(): Promise<void> {
    if (this.sbis.length > 0) {
      await this.router.navigate(['secure/dashboard/sbi'], {
        queryParams: {
          receiverId: this.sbis[0].receiver_id,
          lastReviewEndDate: this.formatDate(this.lastReviewEndDate),
          lastReviewStartDate: this.formatDate(this.lastReviewStartDate)
        }
      });
    }
  }

  formatDate(givenDate: string | null): string {
    if (!givenDate) return '';
    const date = new Date(givenDate);
    const year: number = date.getFullYear();
    const month: string = String(date.getMonth() + 1).padStart(2, '0'); // mese va da 0 a 11
    const day: string = String(date.getDate()).padStart(2, '0');

    return `${year}/${month}/${day}`;
  }

  async goToSbi(sbi: Sbi): Promise<void> {
    const navigationExtras: any = {
      fragment: sbi.id.toString()
    };

    navigationExtras.queryParams = {
      ...(navigationExtras.queryParams || {}),
      lastReviewStartDate: sbi.reported_at,
      receiver_id: sbi.receiver_id
    };

    await this.router.navigate(['/secure/dashboard/sbi'], navigationExtras);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
